import api from './api';
import { Task, TaskFormResponse } from '../types';
import { TaskSubmission } from './taskSubmissionService';

export const courseTaskService = {
  // Admin: Course Tasks
  listCourseTasksAdmin: async (params?: {
    video_id?: number;
    chapter_id?: number;
    course_id?: number;
  }): Promise<{ tasks: Task[] }> => {
    const response = await api.get('/course-tasks/admin', { params });
    return response.data;
  },

  createCourseTask: async (data: {
    title: string;
    description?: string;
    points: number;
    video_id?: number;
    chapter_id?: number;
    task_type?: Task['task_type'];
    requirements?: { [key: string]: any };
    is_active?: boolean;
    order?: number;
  }, voiceFiles?: { choiceVoiceFiles?: Record<string, File | null>; introVoiceFile?: File | null; completionVoiceFile?: File | null }): Promise<{ task: Task }> => {
    // If multiple_choice, always use FormData (even without voice files) to ensure requirements are parsed correctly
    if (data.task_type === 'multiple_choice') {
      const formData = new FormData();
      formData.append('title', data.title);
      if (data.description) formData.append('description', data.description);
      formData.append('points', String(data.points));
      formData.append('task_type', data.task_type);
      formData.append('is_active', String(data.is_active));
      formData.append('order', String(data.order || 0));
      if (data.video_id) formData.append('video_id', String(data.video_id));
      if (data.chapter_id) formData.append('chapter_id', String(data.chapter_id));
      if (data.requirements) {
        formData.append('requirements', JSON.stringify(data.requirements));
      }
      
      // Add voice files if provided
      if (voiceFiles) {
        if (voiceFiles.choiceVoiceFiles) {
          Object.entries(voiceFiles.choiceVoiceFiles).forEach(([choiceId, file]) => {
            if (file) {
              formData.append(`choice_${choiceId}_voice`, file);
            }
          });
        }
        if (voiceFiles.introVoiceFile) {
          formData.append('intro_popup_voice', voiceFiles.introVoiceFile);
        }
        if (voiceFiles.completionVoiceFile) {
          formData.append('completion_popup_voice', voiceFiles.completionVoiceFile);
        }
      }
      
      const response = await api.post('/course-tasks/admin', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    }
    
    // Otherwise, use JSON
    const response = await api.post('/course-tasks/admin', data);
    return response.data;
  },

  updateCourseTask: async (taskId: number, data: Partial<Task>, voiceFiles?: { choiceVoiceFiles?: Record<string, File | null>; introVoiceFile?: File | null; completionVoiceFile?: File | null }): Promise<{ task: Task }> => {
    // If multiple_choice, always use FormData (even without voice files) to ensure requirements are parsed correctly
    if (data.task_type === 'multiple_choice') {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (key === 'requirements' && typeof value === 'object') {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, String(value));
          }
        }
      });
      
      // Add voice files if provided
      if (voiceFiles) {
        if (voiceFiles.choiceVoiceFiles) {
          Object.entries(voiceFiles.choiceVoiceFiles).forEach(([choiceId, file]) => {
            if (file) {
              formData.append(`choice_${choiceId}_voice`, file);
            }
          });
        }
        if (voiceFiles.introVoiceFile) {
          formData.append('intro_popup_voice', voiceFiles.introVoiceFile);
        }
        if (voiceFiles.completionVoiceFile) {
          formData.append('completion_popup_voice', voiceFiles.completionVoiceFile);
        }
      }
      
      const response = await api.put(`/course-tasks/admin/${taskId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    }
    
    // Otherwise, use JSON
    const response = await api.put(`/course-tasks/admin/${taskId}`, data);
    return response.data;
  },

  deleteCourseTask: async (taskId: number): Promise<void> => {
    await api.delete(`/course-tasks/admin/${taskId}`);
  },

  submitCourseTask: async (
    videoId: number,
    taskId: number,
    options?: { file?: File | null; submissionText?: string; responses?: TaskFormResponse[]; choiceId?: string }
  ) => {
    // For multiple choice, use JSON
    if (options?.choiceId) {
      const response = await api.post(`/course-tasks/video/${videoId}/tasks/${taskId}/submit`, {
        choice_id: options.choiceId,
      });
      return response.data;
    }
    
    // For upload/form tasks, use FormData
    const formData = new FormData();
    if (options?.file) {
      formData.append('file', options.file);
    }
    if (options?.submissionText) {
      formData.append('submission_text', options.submissionText);
    }
    if (options?.responses) {
      formData.append('responses', JSON.stringify(options.responses));
    }
    const response = await api.post(`/course-tasks/video/${videoId}/tasks/${taskId}/submit`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  listSubmissionsAdmin: async (params?: { course_id?: number; video_id?: number; status?: string }) => {
    const response = await api.get<{ submissions: TaskSubmission[] }>('/course-tasks/admin/submissions', { params });
    return response.data;
  },

  reviewSubmission: async (submissionId: number, payload: { status: 'approved' | 'rejected'; feedback?: string }) => {
    const response = await api.put<{ message: string; submission: TaskSubmission; points_awarded?: number; points_deducted?: number }>(
      `/course-tasks/admin/submissions/${submissionId}/review`,
      payload
    );
    return response.data;
  },

  penalizeSubmission: async (submissionId: number, payload: { points?: number; reason?: string }) => {
    const response = await api.post<{ message: string; submission: TaskSubmission }>(
      `/course-tasks/admin/submissions/${submissionId}/penalize`,
      payload
    );
    return response.data;
  },

  // User: Course Tasks by video
  listCourseTasksForVideo: async (videoId: number): Promise<{ tasks: Task[] }> => {
    const response = await api.get(`/course-tasks/video/${videoId}`);
    return response.data;
  },
};

